import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import {
  BudgetTranslationStrings,
  distributeItems,
  getStackIDs,
  randomiseTopics,
  selectedStyleAtom,
  shortTimeAgo,
  tabsApiAtom,
} from "../constants/budget-atom";
import HtmlRenderer from "../elements/html-renderer";
import Image from "next/image";
import {
  useBudgetMiniStacksQuery,
  useRelevantNewsQuery,
} from "@graphql/generated/graphql";
import { useRouter } from "next/router";
import Link from "next/link";

import { kBudgetConfig } from "../constants/budget-translations";
import BudgetActionButtons from "./budget-action-buttons";
import SimpleHorizontalScroll from "@components/core/simple-horizontal-scroll";

const BudgetChatResponse: React.FC<{
  messageId: string;
  response: string[];
  payloadTopics: string[];
  onTopicSelected: (number) => void;
  viewedTopics: string[];
}> = ({
  messageId,
  response,
  payloadTopics,
  onTopicSelected,
  viewedTopics,
}) => {
  const selectedStyle = useAtomValue(selectedStyleAtom);
  /// Ensure each response has its own set of topics
  /// Use shared viewedTopics state if shared
  const [suggestedTopics] = useState(
    randomiseTopics(viewedTopics, selectedStyle)
  );
  const copies = useMemo(() => kBudgetConfig(selectedStyle), [selectedStyle]);

  return (
    <div className="flex flex-col space-y-2 font-poppins text-sm">
      {response.map((content, index) => (
        <div key={index}>
          <HtmlRenderer html={content} />
        </div>
      ))}
      <BudgetActionButtons messageId={messageId} />
      <SuggestedTopics
        suggestedTopics={suggestedTopics}
        onTopicSelected={onTopicSelected}
        copies={copies}
      />
      <BudgetChatResponseOptions copies={copies} />
      <RelevantReads messageId={messageId} copies={copies} />
      <RelevantNewsStacks
        messageId={messageId}
        copies={copies}
        payloadTopics={payloadTopics}
      />
    </div>
  );
};

const SuggestedTopics: React.FC<{
  suggestedTopics: any[];
  onTopicSelected: (number) => void;
  copies: BudgetTranslationStrings;
}> = ({ suggestedTopics, onTopicSelected, copies }) => {
  const distributedItems = distributeItems(suggestedTopics, 3, 5);

  return (
    <>
      {distributedItems.length && (
        <div className="flex flex-col space-y-2">
          <p className="mt-4 font-poppins text-lg font-bold text-text-darkest">
            {copies.kExploreMoreTitle}
          </p>
          <SimpleHorizontalScroll>
            <div className="flex flex-col space-y-2">
              {distributedItems.map((topic, index) => (
                <div key={index} className="flex flex-row space-x-2">
                  {distributedItems[index].map((topic) => (
                    <div
                      key={topic.id}
                      onClick={() => onTopicSelected([topic.key])}
                      className="transition-color cursor-pointer whitespace-nowrap rounded-2xl bg-off-white p-4 text-center duration-300 hover:bg-light-background active:bg-secondary"
                    >
                      {topic.emoji} {topic.title}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </SimpleHorizontalScroll>
        </div>
      )}
    </>
  );
};

const RelevantReads: React.FC<{
  messageId: string;
  copies: BudgetTranslationStrings;
}> = ({ messageId, copies }) => {
  const selectedStyle = useAtomValue(selectedStyleAtom);

  const getLanguageCode = () => {
    switch (selectedStyle) {
      case "manglish":
      case "genz":
      case "casual":
        return "en";
      case "bahasa_malaysia":
        return "ms";
      case "chinese":
        return "zh";
    }
  };

  const {
    data: data,
    error: error,
    loading: loading,
  } = useRelevantNewsQuery({
    variables: {
      id: "budget_2026",
      languageCode: getLanguageCode(),
      limit: 10,
    },
  });

  const relevantNews = useMemo(() => {
    if (!data?.category?.news) return [];
    return [...data.category.news];
  }, [data]);

  return (
    <div className="flex flex-col">
      <p className="mt-4 font-poppins text-lg font-bold">
        {copies.kRelevantReadsTitle}
      </p>
      <div>
        {loading && (
          <p className="font-xl font-bold text-text-medium">
            {copies.kLoadingText}
          </p>
        )}
        {error && (
          <p className="font-xl self-center font-bold text-text-medium">
            {copies.kErrorMessage}
          </p>
        )}
        <SimpleHorizontalScroll>
          {relevantNews &&
            relevantNews.map((news, index) => (
              <Link
                key={`${news.id}#message_${messageId}#index_${index}`}
                href={`/news/${news.id}`}
                passHref={true}
              >
                <div id="budget-news-carousel-item">
                  <Image
                    className="rounded-t-xl"
                    src={
                      news.thumbnailUrl ||
                      "https://images.luminews.my/v60ie228o2dhs6472pw40v4sacp1"
                    }
                    alt={news.title}
                    width={200}
                    height={132}
                    objectFit="cover"
                  />
                  <div className=" text-start mx-4 mb-4 mt-2 flex flex-col space-y-1">
                    <span className="text-md font-poppins font-bold line-clamp-3">
                      {news.title}
                    </span>
                    <span className="text-xs line-clamp-1">
                      <span className="font-sen text-text-medium">
                        {news.newsPublisher.name}{" "}
                      </span>{" "}
                      <span className="text-text-lightest">
                        {news.pubDate
                          ? shortTimeAgo(new Date(news.pubDate))
                          : ""}
                      </span>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
        </SimpleHorizontalScroll>
      </div>
    </div>
  );
};

const RelevantNewsStacks: React.FC<{
  messageId: string;
  copies: BudgetTranslationStrings;
  payloadTopics: string[];
}> = ({ messageId, copies, payloadTopics }) => {
  const selectedStyle = useAtomValue(selectedStyleAtom);
  const miniStackIds = getStackIDs(selectedStyle, payloadTopics);
  const { data, loading, error } = useBudgetMiniStacksQuery({
    variables: { ids: miniStackIds },
  });

  const newsStacks = data?.categoriesWithIds ?? [];

  return (
    <>
      <div className="font-poppins text-lg font-bold text-text-darkest">
        {copies.kRelevantNewsStacksTitle}
      </div>
      {loading && !error && (
        <p className="font-xl font-bold text-text-medium">
          {copies.kLoadingText}
        </p>
      )}
      {error && (
        <p className="font-xl self-center font-bold text-text-medium">
          {copies.kErrorMessage}
        </p>
      )}

      <div className="transition-all duration-300 ease-out">
        <SimpleHorizontalScroll>
          {newsStacks &&
            newsStacks.map((newsStack, index) => (
              <Link
                key={`${newsStack.id}#message_${messageId}#index_${index}`}
                href={`/news-stack/${newsStack.slug}`}
                passHref={true}
              >
                <div id="budget-news-stack-carousel-item">
                  <div className="relative h-[180px] w-[150px]">
                    <Image
                      src={
                        newsStack.imageUrl ||
                        "https://images.luminews.my/v60ie228o2dhs6472pw40v4sacp1"
                      }
                      alt={newsStack.exploreName}
                      objectFit="cover"
                      height={180}
                      width={150}
                      className="rounded-lg"
                    />

                    <div className="align-end absolute inset-0 flex items-end justify-start p-4">
                      <p className="text-base font-bold text-white line-clamp-3">
                        {newsStack.exploreName}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
        </SimpleHorizontalScroll>
      </div>
    </>
  );
};

const BudgetChatResponseOptions: React.FC<{
  copies: BudgetTranslationStrings;
}> = ({ copies }) => {
  const tabsApi = useAtomValue(tabsApiAtom);

  return (
    <div id="budget-container-width" className="flex flex-col space-y-2">
      <p className="mt-4 font-poppins text-lg font-bold text-text-darkest">
        {copies.kResponseOptionsTitle}
      </p>
      <div
        className="transition-color w-full cursor-pointer self-center rounded-2xl bg-off-white p-4 text-center duration-300 hover:bg-gray-200 active:bg-gray-200"
        onClick={() => tabsApi.scrollPrev()}
      >
        {copies.kResponseGoBack}
      </div>
      <div
        className="transition-color w-full cursor-pointer self-center rounded-2xl bg-off-white p-4  text-center duration-300 hover:bg-gray-200 active:bg-gray-200"
        onClick={() => tabsApi.scrollTo(0)}
      >
        {copies.kResponsePickAnotherStyle}
      </div>
      <Link href="/topics/budget_2026" passHref>
        <div className="transition-color w-full cursor-pointer self-center rounded-2xl bg-off-white p-4 text-center duration-300 hover:bg-gray-200 active:bg-gray-200">
          {copies.kResponseGoLanding}
        </div>
      </Link>
    </div>
  );
};

export default BudgetChatResponse;
